import * as babylon from "@babylonjs/core";
import { isEqual } from "lodash";

import * as schema from "@/shared/state/visualization/schema/v1/three-d";

import { BabylonState } from "../../state";

/**
 * Scene-derived radius envelope for the third-person camera. `max`
 * scales with the union of all entity bounding boxes — third-person has
 * no tracked entity, but the union captures the same scene scale so
 * zooming out reveals comparable content to what orbit would show.
 *
 * `max` uses a `boundingRadius * 41` multiplier, mirroring orbit-path's
 * `minRadius + maxRadius = (boundingRadius + 0.1) + boundingRadius * 40`.
 * Orbit-path is the right analogue: both modes are scoped to "the
 * spatial extent of content" rather than to "a single small object,"
 * so the multiplier should be calibrated to the scene's own extent, not
 * to 1000x it. The earlier `* 1000` was inherited from orbit-mesh
 * (where `boundingRadius` is a small followed entity) and produced
 * inflated `maxZ`, `maxDrawDistance`, and grid `totalLength` downstream
 * — see `ENG-2233/ENG-2233-perf-issues-investigation.md` findings #1,
 * #4, #8, and #10 for the analysis.
 *
 * `min` scales with scene content rather than sitting at orbit's
 * anti-intersection floor (`boundingRadius * 2 + 0.1`). Orbit's lower
 * bound keeps the camera outside the followed object's bounding sphere;
 * third-person's focus point is abstract, with no object to intersect.
 * The chosen floor `max(0.5, boundingRadius * 0.1)` prevents the camera
 * from approaching the focus point so closely that it appears to pass
 * through nearby content (or, with smoothing residue, through the focus
 * itself), while still allowing close-up framing of a small object
 * within a larger scene at roughly 10% of scene extent.
 *
 * Empty-scene fallback: `boundingRadius = 1`, yielding `[0.5, 41.5]`.
 *
 * Exported so `buildThirdPersonCamera` seeds the singleton `camera.zoom`
 * from the same source as the per-frame recomputation here.
 */
export function computeThirdPersonZoom(state: BabylonState): {
  min: number;
  max: number;
} {
  const generation = state.sceneConfig.entityGeneration ?? 0;
  const cache = state.sceneConfig.thirdPersonZoomCache;
  if (cache && cache.generation === generation) {
    return cache.value;
  }
  let unionMin: babylon.Vector3 | undefined;
  let unionMax: babylon.Vector3 | undefined;
  for (const entity of Object.values(state.entities)) {
    for (const mesh of [entity.mesh?.mesh, entity.path?.mesh]) {
      if (!mesh) {
        continue;
      }
      const bb = mesh.getBoundingInfo().boundingBox;
      if (!unionMin || !unionMax) {
        unionMin = bb.minimumWorld.clone();
        unionMax = bb.maximumWorld.clone();
      } else {
        unionMin = babylon.Vector3.Minimize(unionMin, bb.minimumWorld);
        unionMax = babylon.Vector3.Maximize(unionMax, bb.maximumWorld);
      }
    }
  }
  const boundingRadius =
    unionMin && unionMax
      ? Math.max(...unionMax.subtract(unionMin).scale(0.5).asArray())
      : 1;
  const min = Math.max(0.5, boundingRadius * 0.1);
  const max = min + boundingRadius * 41;
  const value = { min, max };
  state.sceneConfig.thirdPersonZoomCache = { value, generation };
  return value;
}

/**
 * Constrains the camera's zoom envelope, fog density, and depth
 * clipping each frame.
 *
 * `babylonCamera.target` stays at `Vector3.Zero()` for both camera
 * modes; the follow behavior comes from `updateCamera` writing
 * `camera.focusPoint` from the tracked entity (orbit) or user input
 * (third-person), and `applyRenderOrigin` shifting every mesh by
 * `-focusPoint` so the focus renders at the render-space origin.
 *
 * Zoom limits derive from scene scale: orbit reads the spec envelope
 * when given and otherwise infers from the followed entity's bounding
 * box; third-person infers from the union of all entity bounding
 * boxes. Draw distance and fog density derive from the resolved
 * envelope so the grid fades smoothly as it approaches the draw
 * distance.
 */
export function constrainCamera(state: BabylonState) {
  const camera = state.sceneConfig.camera;
  if (!camera) {
    return;
  }
  camera.babylonCamera.update();

  const zoom =
    camera.spec.type === schema.CAMERA_TYPE.thirdPerson
      ? computeThirdPersonZoom(state)
      : computeOrbitZoom(state, camera.spec);

  const maxDrawDistance = computeMaxDrawDistance(state);
  // Formulas for fog transparency available here: https://learn.microsoft.com/en-us/previous-versions/windows/desktop/bb324452(v=vs.85)
  // Anything further than the distance where the fog becomes nearly opaque
  // is effectively not visible.
  const NEARLY_OPAQUE = 0.01;
  state.context.scene.fogDensity =
    Math.log(1 / NEARLY_OPAQUE) / maxDrawDistance;

  const babylonCamera = camera.babylonCamera;
  babylonCamera.minZ = zoom.min > 0 ? zoom.min / 10 : 0.1;
  babylonCamera.maxZ = Math.max(10000, zoom.max * 1000);
  babylonCamera.radius = Math.max(
    Math.min(babylonCamera.radius, zoom.max),
    zoom.min,
  );

  if (!isEqual(zoom, camera.zoom)) {
    // The computed zoom ranges and drawing distances have changed.
    camera.zoom = zoom;

    // TODO: broadcast this computed value to the react state
    // to be able to show the default value in the panel settings.

    // Update the grid to draw to the right size.
    if (state.sceneConfig.grid) {
      state.sceneConfig.grid.load.state = "stale";
    }
  }
}

/**
 * Resolves the maximum draw distance per camera mode.
 *
 * Orbit-`mesh` derives from the followed entity's bounding radius;
 * orbit-`path` from the trajectory's bounding radius (and uses a
 * tighter multiplier so fog tracks typical viewing distance along the
 * path). Third-person mirrors orbit-`mesh`'s formula against the
 * first entity with a mesh component — this gives the two modes
 * matching fog behavior for the same scene without coupling
 * third-person's fog to its (larger) scene-union zoom envelope.
 *
 * Empty-scene / no-mesh fallback: `boundingRadius = 1`, mirroring
 * `computeThirdPersonZoom`.
 *
 * The exhaustive switch forces a future camera type to declare its
 * own draw distance.
 */
export function computeMaxDrawDistance(state: BabylonState): number {
  const camera = state.sceneConfig.camera!;
  const { spec, zoom } = camera;
  switch (spec.type) {
    case schema.CAMERA_TYPE.orbit:
      return spec.target.mesh === "mesh" ? zoom.max * 10 : zoom.max * 2;
    case schema.CAMERA_TYPE.thirdPerson: {
      // First entity with a mesh component, mirroring
      // `resolveFallbackEntity`. `Object.values()[0]` is iteration-
      // order-dependent and may not have a mesh, so iterate.
      let mesh: babylon.AbstractMesh | undefined;
      let pathMesh: babylon.AbstractMesh | undefined;
      for (const entity of Object.values(state.entities)) {
        if (entity.mesh) {
          mesh = entity.mesh.mesh;
          pathMesh = entity.path?.mesh;
          break;
        }
      }
      const boundingRadius = mesh
        ? Math.max(
            ...mesh.getBoundingInfo().boundingBox.extendSizeWorld.asArray(),
          )
        : 1;
      const pathRadius = pathMesh
        ? Math.max(
            ...pathMesh.getBoundingInfo().boundingBox.extendSizeWorld.asArray(),
          )
        : boundingRadius;
      const minRadius = boundingRadius * 2 + 0.1;
      const maxRadius = Math.max(boundingRadius * 1000, pathRadius * 5);
      return (minRadius + maxRadius) * 10;
    }
    default: {
      const exhaustive: never = spec;
      throw new Error(
        `Unhandled camera type: ${(exhaustive as schema.Camera).type}`,
      );
    }
  }
}

function computeOrbitZoom(
  state: BabylonState,
  spec: schema.OrbitCamera,
): { min: number; max: number } {
  const target = spec.target;
  const targetMesh = state.entities[target.id]?.[target.mesh]?.mesh;
  const targetPath = state.entities[target.id]?.path?.mesh;
  const boundingRadius = Math.max(
    ...(targetMesh?.getBoundingInfo().boundingBox.extendSizeWorld.asArray() ?? [
      1,
    ]),
  );
  const pathRadius =
    target.mesh === "path" || !targetPath
      ? boundingRadius
      : Math.max(
          ...(targetPath
            ?.getBoundingInfo()
            .boundingBox.extendSizeWorld.asArray() ?? [1]),
        );

  const minRadius =
    target.mesh === "mesh" ? boundingRadius * 2 + 0.1 : boundingRadius + 0.1;
  const maxRadius =
    target.mesh === "mesh"
      ? Math.max(boundingRadius * 1000, pathRadius * 5)
      : boundingRadius * 40;
  return {
    min: spec.zoom.min ?? minRadius,
    max: spec.zoom.max ?? minRadius + maxRadius,
  };
}
